import { NextRouter, useRouter } from 'next/router';
import { useCallback } from 'react';
import { authDataToWorkspaceList } from 'utils/users';
import { isAppResourceRoute, isExploreRoute, replaceUserOrOrgIdInUrl } from 'utils/urlUtils';
import { LinkProps } from 'next/link';
import { TwoFactorAuthData, useAuthState } from '../AuthContext';

type SwitchUserAccount = {
  (userOrOrgId: string | undefined, passHrefToSwitch: true): LinkProps['href'];
  (userOrOrgId?: string, passHrefToSwitch?: false): void;
};

export const getIsEntityOwner = ({ userId }: Record<string, string>): boolean => {
  const user = useCurrentUser();
  const currentUserId = user ? user.user_id : '';
  return currentUserId === userId;
};

export function useListWorkspaces(): CF.LocalUser[] {
  const { authData } = useAuthState();
  return authDataToWorkspaceList(authData);
}

/**
 * Checks against the userOrOrgId in the URL if the logged-in user has a matching workspace
 */
export function useDoesUserHaveWorkspace(): boolean {
  const router = useRouter();
  const workspaces = useListWorkspaces();

  // The workspace list is for the logged-in user, so we're checking against the userOrOrgId in the URL for access:
  const currentUserOrOrgId = router.query?.userOrOrgId as string;
  return workspaces.some((workspace) => workspace.user_id === currentUserOrOrgId);
}

/**
 * Returns the user account which is currently being used (either logged-in user or org account).
 *
 * NOTE: If you need to use the logged-in user details always, use "useAuthState" hook instead.
 *
 */
export function useCurrentUser(): CF.LocalUser | undefined {
  const currentUserId = useCurrentUserId();
  const workspaces = useListWorkspaces();

  return workspaces.find((workspace) => workspace.user_id === currentUserId);
}

export function useLoggedInUserId(): string {
  const { authData } = useAuthState();

  return authData?.user_id || '';
}

export function useTwoFactorAuthData(): TwoFactorAuthData | undefined {
  const { twoFactorAuthData } = useAuthState();
  return twoFactorAuthData;
}

/**
 * Warning: This function returns empty string if no logged in user.
 */

export function useCurrentUserId(): string {
  const router = useRouter();
  const { authData } = useAuthState();
  const workspaces = useListWorkspaces();
  const userIdFromRouter = router?.query?.userOrOrgId;
  const workspaceExists = workspaces.some((workspace) => workspace.user_id === userIdFromRouter);

  if (userIdFromRouter && typeof userIdFromRouter === 'string' && workspaceExists) {
    return userIdFromRouter;
  }

  return authData?.user_id || '';
}

export function useIsLoggedIn(): boolean {
  const { authData } = useAuthState();
  return Boolean(authData);
}

export function useIsInternalUser(): boolean {
  const { authData } = useAuthState();
  const isInternalUser = authData?.email?.toLowerCase().endsWith('@clarifai.com');
  return Boolean(isInternalUser);
}

export function useIsLoggedInUser(): boolean {
  const currentUserId = useCurrentUserId();
  const loggedInUserId = useLoggedInUserId();
  return currentUserId === loggedInUserId;
}

/**
 * Checks if current user account is an organization account or logged-in user account
 * (Optional) userId: checks for this userId instead of current userId
 */
export function useIsOrgAccount(userId?: string): boolean {
  const { authData } = useAuthState();
  const currentUserId = useCurrentUserId();
  const orgIds = authData?.organizations?.map(({ organization }) => organization.id);

  if (userId) {
    return Boolean(orgIds?.includes(userId));
  }

  const currentUserIdIsOrgId = currentUserId && currentUserId !== authData?.user_id && orgIds?.includes(currentUserId);
  return Boolean(currentUserIdIsOrgId);
}

export function useUserNeeds2FA(): boolean {
  const { authData, twoFactorAuthData } = useAuthState();
  return Boolean(!authData?.session_token && twoFactorAuthData?.two_factor_auth_enabled);
}

export function useIsOnboardingPending(): boolean {
  const { authData } = useAuthState();
  return Boolean(authData?.is_onboarding_pending);
}

export function useSwitchUserAccount(): SwitchUserAccount {
  const router = useRouter();

  const switchUser = useCallback(
    (userOrOrgId?: string, passHrefToSwitch: boolean = false) => {
      const href = getWorkspaceHrefToSwitch({ router, workspaceId: userOrOrgId });

      if (passHrefToSwitch) {
        return href;
      }

      router.push(href);
      return undefined;
    },
    [router],
  );

  return switchUser as SwitchUserAccount;
}

function getWorkspaceHrefToSwitch({ router, workspaceId }: { router: NextRouter; workspaceId?: string }): LinkProps['href'] {
  if (!workspaceId) {
    return '';
  }

  // Change this to the default page to redirect to:
  const defaultPageToRedirectTo = 'home';

  // Check if we're currently on a user/org-scoped page using the same logic as WorkspaceSwitcher
  const isAppResource = isAppResourceRoute(router);
  const isExplorePath = isExploreRoute(router);

  // Redirect to /[userOrOrgId]/home if user is on an app resource route:
  if (isAppResource || isExplorePath) {
    return `/${workspaceId}/${defaultPageToRedirectTo}`;
    // Update the userOrOrgId in place:
  }

  // For 404 cases and pathname without userOrOrgId, we need to construct the new URL from asPath
  if (!router.query?.userOrOrgId) {
    return replaceUserOrOrgIdInUrl(router, workspaceId);
  }

  return {
    pathname: router.pathname,
    query: { ...router.query, userOrOrgId: workspaceId },
  };
}

export * from './useAuthBroadcastChannel';
export * from './useBroadcastRefetchLogin';
export * from './scopes';
export * from './useUrlWithParams';
export * from './useUtmFields';
export * from './usePreserveUtmParams';
